import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";

interface HeroCarouselProps {
  items: Video[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[560px]"
      onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchStart === null) return;
        const diff = e.changedTouches[0].clientX - touchStart;
        if (Math.abs(diff) > 50) go(diff < 0 ? index + 1 : index - 1);
        setTouchStart(null);
      }}
    >
      {items.map((video, i) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={video.banner || video.cover}
            alt={`${video.title} 剧照`}
            title={`${video.title} 在线观看`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
              <div className="max-w-xl animate-fade-up">
                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-md bg-primary-500 px-2 py-1 font-medium text-background-50">
                    {video.type}
                  </span>
                  {video.tags.slice(0, 2).map((t) => (
                    <span
                      key={t}
                      className="rounded-md border border-background-300/40 bg-black/30 px-2 py-1 text-foreground-800"
                    >
                      {t}
                    </span>
                  ))}
                  <span className="flex items-center gap-1 text-accent-400">
                    <i className="ri-star-fill text-[11px]"></i>
                    {video.rating.toFixed(1)}
                  </span>
                </div>
                <h2 className="font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
                  {video.title}
                </h2>
                <p className="mt-3 line-clamp-2 max-w-lg text-sm leading-relaxed text-foreground-700 md:text-base">
                  {video.synopsis}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${video.id}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    立即观看
                  </Link>
                  <Link
                    to={`/detail/${video.id}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/50 bg-black/30 px-6 py-3 text-sm font-medium text-foreground-900 transition hover:border-primary-400 hover:text-primary-400"
                  >
                    <i className="ri-information-line text-lg"></i>
                    查看详情
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-300/40 bg-black/40 text-foreground-900 transition hover:bg-primary-500 hover:text-background-50 md:flex"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-300/40 bg-black/40 text-foreground-900 transition hover:bg-primary-500 hover:text-background-50 md:flex"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {items.map((v, i) => (
          <button
            key={v.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? "w-8 bg-primary-500" : "w-4 bg-background-400/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
}